import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Divider, Grid, Paper, Stack, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  CancelOutlined as CancelOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  EventOutlined as EventOutlinedIcon,
  TimelapseOutlined as TimelapseOutlinedIcon,
  VerifiedUser as VerifiedUserIcon,
  ErrorOutline as ErrorOutlineIcon,
  CalendarToday as CalendarTodayIcon,
} from "@mui/icons-material";
import { uploadLicense, getLicenseInfo } from "features/service/service.slice";

const LicenseInfoCard = ({ showDetails = true }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { license, appConstants } = useSelector((state) => state.service);
  const LICENSE_STATUS = appConstants?.allTypes?.LICENSE_STATUS || {};
  const fileInputRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSuccess, setDialogSuccess] = useState(true);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");

  useEffect(() => {
    dispatch(getLicenseInfo());
  }, [dispatch]);

  const handleUploadLicenseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };
const formatDate = (date) => {
  if (!date) return "-";

  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
  const handleLicenseFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    const licenseFile = new File([file], "license.dat", {
      type: file.type || "application/octet-stream",
    });
    formData.append("license", licenseFile);

    const resultAction = await dispatch(uploadLicense(formData));
    if (uploadLicense.fulfilled.match(resultAction)) {
      const payload = resultAction.payload || {};
      const success = payload.success === true;
      const message = payload.message || (success ? "License updated successfully" : "Something went wrong.");
      setDialogSuccess(success);
      setDialogTitle(success ? "License Updated" : "License Upload Failed");
      setDialogMessage(message);
      setDialogOpen(true);
      if (success) dispatch(getLicenseInfo({ force: true }));
    } else {
      const errMsg = resultAction.payload || resultAction.error?.message || "Failed to upload license";
      setDialogSuccess(false);
      setDialogTitle("License Upload Failed");
      setDialogMessage(errMsg);
      setDialogOpen(true);
    }
  };

  return (
    <Paper
      sx={{
        borderRadius: 3,
        mb: 2,
        p: 2,
        mt: 1,
        boxShadow: theme.shadows[2],
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <VerifiedUserIcon
            sx={{ fontSize: 40, color: theme.palette.primary.main }}
          />
          <Typography variant="subtitle1" fontWeight={800}>
            License Information
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" columnGap={1.5}>
          <input
            type="file"
            accept=".dat"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleLicenseFileChange}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleUploadLicenseClick}
            disabled={license?.isUploading}
            sx={{ textTransform: "none", px: 1.5, boxShadow: "none" }}
          >
            Upload License
          </Button>
        </Stack>
      </Stack>
      {showDetails && (
        <>
          <Divider sx={{ mb: 1 }} />
          <Grid container spacing={1}>
            {/* STATUS */}
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1} alignItems="center">
                {license?.licenseStatus === LICENSE_STATUS.ACTIVE ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
                ) : (
                  <CancelOutlinedIcon sx={{ fontSize: 20, color: theme.palette.error.main }} />
                )}

                <Typography variant="body1">
                  <b>Status :</b>{" "}
                  <span
                    style={{
                      color:
                        license?.licenseStatus === LICENSE_STATUS.ACTIVE
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                      fontWeight: 600,
                    }}
                  >
                    {license?.licenseStatus || "Unknown"}
                  </span>
                </Typography>
              </Stack>
            </Grid>

            {/* CUSTOMER */}
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonOutlineIcon sx={{ fontSize: 20, color: theme.palette.info.main }} />

                <Typography variant="body1">
                  <b>Customer :</b> {license?.licenseInfo?.customerName || "-"}
                </Typography>
              </Stack>
            </Grid>

            {/* EXPIRY */}
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1} alignItems="center">
                <EventOutlinedIcon sx={{ fontSize: 20, color: theme.palette.warning.main }} />

                <Typography variant="body1">
                  <b>Expiry :</b> {formatDate(license?.licenseInfo?.expiryDate)}
                </Typography>
              </Stack>
            </Grid>

            {/* DAYS */}
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TimelapseOutlinedIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />

                <Typography variant="body1">
                  <b>Days Left :</b> {license?.daysUntilExpiry ?? "-"}
                </Typography>
              </Stack>
            </Grid>

            {/* ISSUED AT */}
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarTodayIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />

                <Typography variant="body1">
                  <b>Issued At :</b> {formatDate(license?.licenseInfo?.issuedAt)}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {dialogSuccess ? (
            <CheckCircleOutlineIcon sx={{ color: theme.palette.success.main }} />
          ) : (
            <ErrorOutlineIcon sx={{ color: theme.palette.error.main }} />
          )}
          {dialogTitle}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="contained" color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default LicenseInfoCard;

